// Pure helpers behind the vault screens: names, the greeting, wardrobe facts,
// care symbols, search, sort and scanned-label parsing. No React, no network.
import type { VaultIntent, VaultItem } from '../../services/vaultService';

export const intentOf = (item: VaultItem): VaultIntent => item.intent ?? 'own';

/** "Crew Tee — Ink" → base "Crew Tee", colour "Ink" (a common naming habit, not a rule). */
export const splitName = (name: string): { base: string; colour?: string } => {
  const m = name.match(/^(.+?)\s+[—–]\s+(.{2,24})$/);
  return m ? { base: m[1], colour: m[2] } : { base: name };
};

export const colourOf = (item: VaultItem): string | undefined =>
  item.snapshot.details?.colour || splitName(item.snapshot.name).colour;

/** The caption under a piece: the variant for a numbered item ("Ink · S"), else its colour. */
export const subtitleOf = (item: VaultItem): string | undefined =>
  item.snapshot.variant || colourOf(item);

export { greeting } from '../../utils/greeting';

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** One line about the person's own pieces, under the greeting. */
export const personalLine = (items: VaultItem[]): { count: string; rest?: string } => {
  const count = plural(items.length, 'piece');
  if (items.length < 2) return { count };
  const colours = new Map<string, number>();
  items.forEach(i => {
    const c = colourOf(i);
    if (c) colours.set(c, (colours.get(c) ?? 0) + 1);
  });
  const [top, second] = [...colours.entries()].sort((a, b) => b[1] - a[1]);
  if (top && top[1] >= 2 && (!second || top[1] > second[1])) return { count, rest: `${top[0]} is your colour.` };
  const wanted = items.filter(i => intentOf(i) === 'want').length;
  if (wanted) return { count, rest: `${wanted} on your wishlist.` };
  const brands = new Set(items.map(i => i.snapshot.creatorName).filter(Boolean));
  if (brands.size > 1) return { count, rest: `From ${brands.size} brands.` };
  return { count };
};

// ---- wardrobe facts -----------------------------------------------------------

const COUNTRIES: [RegExp, string][] = [
  [/\b(france)\b/i, 'France'], [/\b(belgium|belgique|belgië)\b/i, 'Belgium'], [/\b(italy|italie|italia)\b/i, 'Italy'],
  [/\b(portugal)\b/i, 'Portugal'], [/\b(spain|espagne|españa)\b/i, 'Spain'], [/\b(germany|allemagne|deutschland)\b/i, 'Germany'],
  [/\b(austria|autriche|österreich)\b/i, 'Austria'], [/\b(poland|pologne|polska)\b/i, 'Poland'],
  [/\b(netherlands|pays-bas|holland)\b/i, 'Netherlands'], [/\b(united kingdom|royaume-uni|uk|england|scotland|wales)\b/i, 'United Kingdom'],
  [/\b(ireland|irlande)\b/i, 'Ireland'], [/\b(denmark|danemark)\b/i, 'Denmark'], [/\b(sweden|suède)\b/i, 'Sweden'],
  [/\b(norway|norvège)\b/i, 'Norway'], [/\b(finland|finlande)\b/i, 'Finland'], [/\b(switzerland|suisse)\b/i, 'Switzerland'],
  [/\b(czech republic|czechia|tchéquie)\b/i, 'Czechia'], [/\b(slovakia|slovaquie)\b/i, 'Slovakia'], [/\b(hungary|hongrie)\b/i, 'Hungary'],
  [/\b(romania|roumanie)\b/i, 'Romania'], [/\b(bulgaria|bulgarie)\b/i, 'Bulgaria'], [/\b(greece|grèce)\b/i, 'Greece'],
  [/\b(lithuania|lituanie)\b/i, 'Lithuania'], [/\b(latvia|lettonie)\b/i, 'Latvia'], [/\b(estonia|estonie)\b/i, 'Estonia'],
  [/\b(turkey|türkiye|turquie)\b/i, 'Türkiye'], [/\b(morocco|maroc)\b/i, 'Morocco'], [/\b(tunisia|tunisie)\b/i, 'Tunisia'],
  [/\b(egypt|égypte)\b/i, 'Egypt'], [/\b(india|inde)\b/i, 'India'], [/\b(bangladesh)\b/i, 'Bangladesh'], [/\b(pakistan)\b/i, 'Pakistan'],
  [/\b(sri lanka)\b/i, 'Sri Lanka'], [/\b(china|chine)\b/i, 'China'], [/\b(vietnam|viet nam|viêt nam)\b/i, 'Vietnam'],
  [/\b(cambodia|cambodge)\b/i, 'Cambodia'], [/\b(indonesia|indonésie)\b/i, 'Indonesia'], [/\b(thailand|thaïlande)\b/i, 'Thailand'],
  [/\b(japan|japon)\b/i, 'Japan'], [/\b(south korea|korea|corée)\b/i, 'South Korea'], [/\b(taiwan)\b/i, 'Taiwan'],
  [/\b(united states|usa|u\.s\.a\.|états-unis)\b/i, 'United States'], [/\b(canada)\b/i, 'Canada'], [/\b(mexico|mexique)\b/i, 'Mexico'],
  [/\b(peru|pérou)\b/i, 'Peru'], [/\b(brazil|brésil)\b/i, 'Brazil'], [/\b(australia|australie)\b/i, 'Australia'],
];

export const countriesIn = (text: string): string[] =>
  [...new Set(COUNTRIES.filter(([re]) => re.test(text)).map(([, name]) => name))];

// ---- care symbols (ISO 3758 style), read from the brand's care notes -------------

export type CareSymbolId =
  | 'wash' | 'w30' | 'w40' | 'w60' | 'wcold' | 'wgentle' | 'whand'
  | 'nobleach' | 'notumble' | 'tumble' | 'line' | 'lineshade' | 'flat' | 'flatshade'
  | 'iron' | 'iron1' | 'iron2' | 'iron3' | 'noiron' | 'dryclean' | 'nodryclean';

type CareGroup = 'wash' | 'bleach' | 'tumble' | 'dry' | 'iron' | 'dryclean';
const GROUP: Record<CareSymbolId, CareGroup> = {
  wash: 'wash', w30: 'wash', w40: 'wash', w60: 'wash', wcold: 'wash', wgentle: 'wash', whand: 'wash',
  nobleach: 'bleach', notumble: 'tumble', tumble: 'tumble', line: 'dry', lineshade: 'dry', flat: 'dry', flatshade: 'dry',
  iron: 'iron', iron1: 'iron', iron2: 'iron', iron3: 'iron', noiron: 'iron', dryclean: 'dryclean', nodryclean: 'dryclean',
};

const NEG = String.raw`(?:do not|don't|never|no|avoid|ne pas|pas de|jamais)`;
const SHADE = /\bshade\b|out of (?:the )?(?:direct )?sun|à l'ombre|ombre/i;

/** Symbols one care note implies. A note can carry several ("No tumble dryer, no bleach"). */
export const careSymbolsFor = (line: string): CareSymbolId[] => {
  const l = line.toLowerCase();
  const out: CareSymbolId[] = [];
  // washing
  if (/hand[- ]?wash|à la main/.test(l)) out.push('whand');
  else if (/\bwash|lavage|laver\b/.test(l) && !new RegExp(`${NEG}\\s+(?:machine )?wash`).test(l)) {
    const t = l.match(/(\d{2})\s*(?:°|degrees|deg)/);
    if (t && ['30', '40', '60'].includes(t[1])) out.push(`w${t[1]}` as CareSymbolId);
    else if (/\bcold|\bcool|froid/.test(l)) out.push(/gentle|delicate|délicat/.test(l) ? 'wgentle' : 'wcold');
    else if (/gentle|delicate|délicat/.test(l)) out.push('wgentle');
    else out.push('wash');
  }
  if (new RegExp(`${NEG}\\s+(?:chlorine )?bleach|sans javel|pas de javel`).test(l)) out.push('nobleach');
  if (new RegExp(`${NEG}\\s+(?:tumble|dryer|drum)|sèche-linge interdit|pas de sèche-linge`).test(l)) out.push('notumble');
  else if (/tumble[- ]dry(?! is)|sèche-linge/.test(l) && !/hot drum|against a hot drum/.test(l)) out.push('tumble');
  // natural drying
  if (/\bflat\b|à plat/.test(l) && /dry|lay|leave|towel|sécher|séchage/.test(l)) out.push(SHADE.test(l) ? 'flatshade' : 'flat');
  else if (
    !/(?:never|don't|do not) hang/.test(l) &&
    /line[- ]dry|hang(?:ing)? (?:it )?(?:to )?dry|dry on a hanger|on a (?:wide )?hanger|hang (?:it )?damp|sécher (?:sur|à l'air)/.test(l)
  )
    out.push(SHADE.test(l) ? 'lineshade' : 'line');
  // ironing ("steam rather than iron" gets no iron symbol: better none than a wrong one)
  if (/(?:do not|don't|never|ne pas) (?:iron|repasser)/.test(l)) out.push('noiron');
  else if (
    /\biron|repass/.test(l) &&
    !/needs? (?:no|almost no) (?:iron|pressing)/.test(l) &&
    !/(?:rather than|instead of|plutôt que de?) (?:an? )?(?:iron|repass)/.test(l)
  ) {
    if (/cool|low|doux|basse/.test(l)) out.push('iron1');
    else if (/warm|medium|moyen/.test(l)) out.push('iron2');
    else if (/\bhot\b|high|élevée/.test(l)) out.push('iron3');
    else out.push('iron');
  }
  if (/dry[- ]?clean|nettoyage à sec/.test(l)) out.push(new RegExp(`${NEG}\\s+dry[- ]?clean`).test(l) ? 'nodryclean' : 'dryclean');
  return out;
};

/** One symbol per care group across all notes (first note wins), in label order. */
export const careSymbols = (lines: string[] = []): CareSymbolId[] => {
  const seen = new Set<CareGroup>();
  const out: CareSymbolId[] = [];
  lines.forEach(line =>
    careSymbolsFor(line).forEach(s => {
      if (seen.has(GROUP[s])) return;
      seen.add(GROUP[s]);
      out.push(s);
    })
  );
  const order: CareGroup[] = ['wash', 'bleach', 'tumble', 'dry', 'iron', 'dryclean'];
  return out.sort((a, b) => order.indexOf(GROUP[a]) - order.indexOf(GROUP[b]));
};

// ---- search & sort --------------------------------------------------------------

const fold = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export const matchesQuery = (item: VaultItem, query: string, folderName?: string): boolean => {
  const q = fold(query.trim());
  if (!q) return true;
  const s = item.snapshot;
  const hay = fold([s.name, s.creatorName, s.collectionName, s.variant, s.serial, s.details?.colour, folderName].filter(Boolean).join(' '));
  return q.split(/\s+/).every(word => hay.includes(word));
};

export type VaultSort = 'recent' | 'name' | 'brand' | 'price';

export const priceValue = (price?: string): number | undefined => {
  if (!price) return undefined;
  const m = price.replace(/\s/g, '').match(/(\d+(?:[.,]\d{1,2})?)/);
  return m ? Number(m[1].replace(',', '.')) : undefined;
};

export const sortItems = (items: VaultItem[], sort: VaultSort): VaultItem[] => {
  const byName = (a: VaultItem, b: VaultItem) => a.snapshot.name.localeCompare(b.snapshot.name);
  const copy = [...items];
  switch (sort) {
    case 'name':
      return copy.sort(byName);
    case 'brand':
      return copy.sort((a, b) => (a.snapshot.creatorName ?? '').localeCompare(b.snapshot.creatorName ?? '') || byName(a, b));
    case 'price':
      return copy.sort((a, b) => {
        const pa = priceValue(a.snapshot.details?.price), pb = priceValue(b.snapshot.details?.price);
        if (pa === undefined && pb === undefined) return byName(a, b);
        if (pa === undefined) return 1;
        if (pb === undefined) return -1;
        return pa - pb || byName(a, b);
      });
    default:
      return copy.sort((a, b) => (a.savedAt < b.savedAt ? 1 : a.savedAt > b.savedAt ? -1 : 0));
  }
};

// ---- scanned labels --------------------------------------------------------------

export type ScanResult =
  | { kind: 'product'; id: string }
  | { kind: 'basiqr'; url: string }
  | { kind: 'other'; text: string };

/**
 * What a scanned QR code points at. Our own product links open in the app;
 * other BasiQR links open as they are; anything else is shown, never opened.
 */
export const parseScan = (raw: string, appOrigin: string = window.location.origin): ScanResult => {
  const text = raw.trim();
  let url: URL;
  try {
    url = new URL(text);
  } catch {
    return { kind: 'other', text };
  }
  const host = url.hostname.toLowerCase();
  let appHost = '';
  try {
    appHost = new URL(appOrigin).hostname.toLowerCase();
  } catch {
    // keep '' — only basiqr.com hosts count then
  }
  const ours = host === appHost || host === 'basiqr.com' || host.endsWith('.basiqr.com');
  if (!ours || (url.protocol !== 'https:' && host !== appHost)) return { kind: 'other', text };
  const m = url.pathname.match(/^\/(?:product|p)\/([A-Za-z0-9-]{6,100})\/?$/);
  if (m) return { kind: 'product', id: m[1] };
  return { kind: 'basiqr', url: url.toString() };
};
